import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { subscribePwa, triggerInstallPrompt, isMobile, isPwaInstalled } from "@/lib/pwaInstall";

const DISMISS_KEY = "lokaso_pwa_install_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const SHOW_DELAY_MS = 30 * 1000; // 30 seconds

export default function InstallPWABanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  // Subscribe to the shared PWA install state (captured globally at module load)
  useEffect(() => subscribePwa(({ deferredPrompt: dp }) => setDeferredPrompt(dp)), []);

  // Decide when to show the banner
  useEffect(() => {
    if (!isMobile() || isPwaInstalled()) return;

    let dismissedAt = 0;
    try {
      dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    } catch (_e) { /* private mode — ignore, show banner */ }
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;

    const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  // Hide if user installs from elsewhere
  useEffect(() => {
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_e) { /* private mode — banner will reappear next session, acceptable */ }
    setVisible(false);
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      const outcome = await triggerInstallPrompt();
      if (process.env.NODE_ENV !== "production" && !outcome) {
        // eslint-disable-next-line no-console
        console.warn("[PWA] install prompt no outcome");
      }
      setDeferredPrompt(null);
      dismiss();
    } else {
      // No native prompt available (iOS Safari, unsupported browser, etc.)
      // Route users to the dedicated /app landing page which handles their
      // exact device with a proper guided experience.
      dismiss();
      window.location.assign("/app");
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 z-[9998] md:hidden"
    >
      <div className="rounded-2xl bg-[#1A5C35] text-[#FAF8F3] shadow-2xl border border-[#E8A020]/40 overflow-hidden">
        <div className="flex items-start gap-3 p-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-[#FAF8F3] flex items-center justify-center">
            <span
              className="font-bold text-xl"
              style={{ color: "#1A5C35", fontFamily: "Fraunces, serif" }}
            >
              L
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm leading-tight">
              Installez Lokaso sur votre téléphone
            </p>
            <p className="text-xs mt-1 text-[#FAF8F3]/85 leading-snug">
              Installez Lokaso sur votre téléphone pour un accès rapide!
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleInstall}
                data-testid="pwa-install-button"
                className="inline-flex items-center gap-1.5 bg-[#E8A020] hover:bg-[#d8911a] active:bg-[#c4810f] text-[#1A1A1A] text-sm font-semibold rounded-full px-4 py-2 transition-colors"
              >
                <Download className="w-4 h-4" />
                Installer
              </button>
              <button
                type="button"
                onClick={dismiss}
                data-testid="pwa-install-dismiss"
                className="text-sm text-[#FAF8F3]/80 hover:text-[#FAF8F3] px-3 py-2"
              >
                Plus tard
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Fermer"
            data-testid="pwa-install-close"
            className="flex-shrink-0 text-[#FAF8F3]/70 hover:text-[#FAF8F3] p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
